import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

/**
 * Loads the Razorpay SDK dynamically if not already available in window
 */
export const loadRazorpaySDK = async () => {
  if (typeof window !== "undefined" && window.Razorpay) {
    return window.Razorpay;
  }
  return new Promise((resolve) => {
    const existing = document.querySelector('script[src*="checkout.razorpay.com"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Razorpay));
      existing.addEventListener("error", () => resolve(null));
      if (window.Razorpay) return resolve(window.Razorpay);
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => resolve(null);
    document.body.appendChild(script);
  });
};

/**
 * Initiates Razorpay checkout for Course Enrollment
 */
export const startRazorpayCourseCheckout = async ({
  courseId,
  user,
  referralCode,
  onSuccess,
  onError,
}) => {
  const dismissToast = toast.loading("Initializing secure Razorpay payment...");

  try {
    const orderRes = await axios.post(
      `${API}/payments/create-order`,
      { course_id: courseId },
      { withCredentials: true }
    );

    const { free, razorpay_order_id, amount, payment, course_title } = orderRes.data;

    if (free) {
      toast.dismiss(dismissToast);
      toast.success("Enrolled successfully in free course!");
      if (onSuccess) onSuccess(orderRes.data);
      return;
    }

    const RazorpayClass = await loadRazorpaySDK();
    if (!RazorpayClass) {
      toast.dismiss(dismissToast);
      toast.error("Razorpay SDK failed to load. Please check your internet connection.");
      if (onError) onError(new Error("Razorpay SDK failed to load"));
      return;
    }

    const keyRes = await axios.get(`${API}/payments/razorpay-key`);
    const keyId = keyRes.data?.key_id;

    toast.dismiss(dismissToast);

    const options = {
      key: keyId,
      amount: amount,
      currency: "INR",
      name: "JamaDhan Institute",
      description: course_title || "Course Enrollment",
      image: "/jamadhan-logo.png",
      order_id: razorpay_order_id,
      prefill: {
        name: user?.name || "",
        email: user?.email || "",
        contact: user?.phone || "",
      },
      theme: {
        color: "#00FF88",
        backdrop_color: "rgba(5, 7, 14, 0.85)",
      },
      handler: async function (response) {
        const verifyToast = toast.loading("Verifying payment and activating enrollment...");
        try {
          const verifyRes = await axios.post(
            `${API}/payments/verify`,
            {
              payment_id: payment.payment_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            },
            { withCredentials: true }
          );
          toast.dismiss(verifyToast);
          toast.success(verifyRes.data?.message || "Payment Successful! Course unlocked.");
          if (onSuccess) onSuccess(verifyRes.data);
        } catch (verErr) {
          toast.dismiss(verifyToast);
          toast.error(verErr.response?.data?.detail || "Payment verification failed.");
          if (onError) onError(verErr);
        }
      },
      modal: {
        ondismiss: function () {
          toast.info("Payment window closed.");
          if (onError) onError(new Error("Payment cancelled"));
        },
      },
    };

    const rzpInstance = new RazorpayClass(options);
    rzpInstance.open();
  } catch (err) {
    toast.dismiss(dismissToast);
    const detail =
      err.response?.data?.detail ||
      err.message ||
      "Payment gateway initialization failed. Please try again.";
    toast.error(detail);
    if (onError) onError(err);
  }
};

/**
 * Backward compatibility alias
 */
export const startCashfreeCheckout = startRazorpayCourseCheckout;
